import { useState, useMemo } from "react";
import { router, Link } from "@inertiajs/react";
import { Box, Flex, Heading, Button, Badge, Text, Container, Table, Link as Clink } from "@chakra-ui/react";
import { event_path, new_event_path } from "@/routes";
import { Event } from "@/types";
import { formatDate, prefectures } from "@/lib/utils";
import { useAuth } from "@/hooks/useAuth";
import Head from "@/components/Head";

interface Props {
  events: Event[];
  currentPage: number;
  totalPages: number;
  currentFilter?: string;
}

const getStatusColorScheme = (statusText: string): string => {
  switch (statusText) {
    case "開催中":
      return "green";
    case "開催予定":
      return "blue";
    case "終了":
      return "gray";
    default:
      return "gray";
  }
};

const handleRowClick = (event: Event) => {
  router.visit(event_path(event.id));
};

export default function Index({ events, currentPage, totalPages, currentFilter = "all" }: Props) {
  const { logged_in } = useAuth();

  const [statusFilter, setStatusFilter] = useState<string>("all");
  const [viewFilter, setViewFilter] = useState<string>(currentFilter);

  const filteredEvents = useMemo(() => {
    const now = new Date();

    switch (statusFilter) {
      case "all":
        return events;
      case "upcoming":
        return events.filter(event => new Date(event.start_at) > now);
      case "ongoing":
        return events.filter(event => event.status === "開催中");
      case "finished":
        return events.filter(event => event.status === "終了");
      default:
        return events;
    }
  }, [events, statusFilter]);

  const handleViewFilterChange = (newFilter: string) => {
    setViewFilter(newFilter);
    router.get("/events", { filter: newFilter === "all" ? undefined : newFilter }, { preserveScroll: true });
  };

  return (
    <>
      <Head title="公開イベント" robots={true} />

      <Container maxW="7xl" py={8}>
        <Box mb={8}>
          {/* ヘッダーセクション */}
          <Flex justify="space-between" align="center" wrap="wrap" gap={4} mb={8}>
            <Box>
              <Heading as="h1" size="lg" color="gray.800" mb={1}>
                公開イベント
              </Heading>
              <Text color="gray.600" fontSize="sm">
                誰でも参加可能なイベントの一覧です
              </Text>
            </Box>
            {logged_in && (
              <Link href={new_event_path()}>
                <Button colorPalette="blue" size="md" shadow="sm" _hover={{ shadow: "md", transform: "translateY(-1px)" }} transition="all 0.2s">
                  イベントを作成
                </Button>
              </Link>
            )}
          </Flex>

          {/* フィルターセクション */}
          <Box p={4} bg="white" borderWidth={1} borderRadius="lg" shadow="sm" mb={8}>
            <Flex align="center" gap={6} wrap="wrap">
              {/* ビューフィルター（ログインユーザーのみ） */}
              {logged_in && (
                <Flex align="center" gap={4}>
                  <Text fontSize="sm" fontWeight="medium" color="gray.700">
                    表示対象:
                  </Text>
                  <select
                    value={viewFilter}
                    onChange={e => handleViewFilterChange(e.target.value)}
                    style={{
                      padding: "8px 12px",
                      border: "1px solid #d1d5db",
                      borderRadius: "6px",
                      backgroundColor: "white",
                      fontSize: "14px",
                      minWidth: "180px",
                    }}
                  >
                    <option value="all">全てのイベント</option>
                    <option value="created">自分が主催したイベント</option>
                    <option value="participating">自分が参加しているイベント</option>
                  </select>
                </Flex>
              )}

              {/* ステータスフィルター */}
              <Flex align="center" gap={4}>
                <Text fontSize="sm" fontWeight="medium" color="gray.700">
                  開催状態:
                </Text>
                <select
                  value={statusFilter}
                  onChange={e => setStatusFilter(e.target.value)}
                  style={{
                    padding: "8px 12px",
                    border: "1px solid #d1d5db",
                    borderRadius: "6px",
                    backgroundColor: "white",
                    fontSize: "14px",
                    minWidth: "140px",
                  }}
                >
                  <option value="all">すべて</option>
                  <option value="upcoming">開催予定</option>
                  <option value="ongoing">開催中</option>
                  <option value="finished">終了済み</option>
                </select>
              </Flex>
            </Flex>
          </Box>

          {/* イベントリスト */}
          <Box bg="white" borderWidth={1} borderColor="gray.200" borderRadius="lg" shadow="sm" overflow="hidden">
            <Box overflowX="auto">
              <Table.Root>
                <Table.Header>
                  <Table.Row>
                    <Table.ColumnHeader textAlign="left" py={4} px={6} borderBottom="solid 1px" borderColor="gray.200">
                      <Flex align="center" gap={2}>
                        <Text fontWeight="semibold" color="gray.700" fontSize="sm">
                          📅 開催日時
                        </Text>
                      </Flex>
                    </Table.ColumnHeader>
                    <Table.ColumnHeader textAlign="left" py={4} px={6} borderBottom="solid 1px" borderColor="gray.200">
                      <Text fontWeight="semibold" color="gray.700" fontSize="sm">
                        🎯 イベント名
                      </Text>
                    </Table.ColumnHeader>
                    <Table.ColumnHeader textAlign="left" py={4} px={6} borderBottom="solid 1px" borderColor="gray.200">
                      <Text fontWeight="semibold" color="gray.700" fontSize="sm">
                        📍 都道府県
                      </Text>
                    </Table.ColumnHeader>
                    <Table.ColumnHeader textAlign="left" py={4} px={6} borderBottom="solid 1px" borderColor="gray.200">
                      <Text fontWeight="semibold" color="gray.700" fontSize="sm">
                        📊 開催状態
                      </Text>
                    </Table.ColumnHeader>
                  </Table.Row>
                </Table.Header>
                <Table.Body>
                  {filteredEvents.length === 0 ? (
                    <Table.Row>
                      <Table.Cell colSpan={4} textAlign="center" py={12}>
                        <Box>
                          <Text color="gray.500" fontSize="4xl" mb={3}>
                            📅
                          </Text>
                          <Box>
                            <Text color="gray.600" fontWeight="medium" mb={1}>
                              イベントがありません
                            </Text>
                            <Text color="gray.500" fontSize="sm">
                              {logged_in ? "新しいイベントを作成してみましょう" : "ログインしてイベントを作成できます"}
                            </Text>
                          </Box>
                        </Box>
                      </Table.Cell>
                    </Table.Row>
                  ) : (
                    filteredEvents.map(event => (
                      <Table.Row
                        key={event.id}
                        _hover={{ bg: "blue.50" }}
                        transition="background-color 0.2s"
                        cursor="pointer"
                        onClick={() => handleRowClick(event)}
                      >
                        <Table.Cell py={4} px={6} borderBottom="solid 1px" borderColor="gray.100">
                          <Text fontWeight="medium" color="gray.900" fontSize="sm">
                            {formatDate(event.start_at)}
                          </Text>
                        </Table.Cell>
                        <Table.Cell py={4} px={6} borderBottom="solid 1px" borderColor="gray.100">
                          {event.name}
                        </Table.Cell>
                        <Table.Cell py={4} px={6} borderBottom="solid 1px" borderColor="gray.100">
                          <Text fontSize="sm" color="gray.600">
                            {event.prefecture ? prefectures[event.prefecture] || event.prefecture : "-"}
                          </Text>
                        </Table.Cell>
                        <Table.Cell py={4} px={6} borderBottom="solid 1px" borderColor="gray.100">
                          <Badge
                            variant="subtle"
                            colorScheme={getStatusColorScheme(event.status)}
                            px={3}
                            py={1}
                            borderRadius="full"
                            fontSize="xs"
                            fontWeight="medium"
                          >
                            {event.status}
                          </Badge>
                        </Table.Cell>
                      </Table.Row>
                    ))
                  )}
                </Table.Body>
              </Table.Root>
            </Box>
          </Box>

          {/* Pagination */}
          {totalPages > 1 && (
            <Box mt={8}>
              <Flex justify="center" gap={2}>
                {Array.from({ length: totalPages }, (_, i) => i + 1).map(page => (
                  <Clink
                    as={Link}
                    key={page}
                    href={`/events?page=${page}${viewFilter !== "all" ? `&filter=${viewFilter}` : ""}`}
                    px={3}
                    py={2}
                    borderRadius="md"
                    bg={page === currentPage ? "blue.500" : "white"}
                    color={page === currentPage ? "white" : "gray.700"}
                    borderWidth={1}
                    borderColor="gray.200"
                    _hover={{ bg: page === currentPage ? "blue.600" : "gray.50" }}
                    fontSize="sm"
                    fontWeight="medium"
                  >
                    {page}
                  </Clink>
                ))}
              </Flex>
            </Box>
          )}
        </Box>
      </Container>
    </>
  );
}
